import React, { useState } from 'react';
import { Calculator } from '../../../types';
import { Icon } from '../../Icon';
import { CalculatorShell } from '../CalculatorShell';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../../utils/formatUtils';

interface FGTSCalculatorProps {
  calculator: Calculator;
  onBack: () => void;
}

const FGTSCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [salary, setSalary] = useState<string>('');
  const [months, setMonths] = useState<string>('');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const sal = parseFloat(parseBrazilianNumber(salary)) || 0;
    const mos = parseInt(parseBrazilianNumber(months)) || 0;
    if (sal <= 0 || mos <= 0) return;

    // Alíquota padrão do FGTS (CLT)
    const rate = 8.0;

    const monthlyDeposit = sal * (rate / 100);
    const totalDeposit = monthlyDeposit * mos;

    // Saque-aniversário (alíquotas por faixa de tempo)
    const withdrawalRates = [
      { years: 1, rate: 50 },
      { years: 2, rate: 40 },
      { years: 3, rate: 30 },
      { years: 4, rate: 20 },
      { years: 5, rate: 10 },
      { years: 6, rate: 5 },
      { years: 7, rate: 0 }
    ];

    const res = {
      salary: sal,
      rate,
      monthlyDeposit,
      totalDeposit,
      months: mos,
      withdrawalRates
    };

    setResultState(res);

    onResultChange([
      { label: 'Salário Base', value: `R$ ${res.salary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Depósito Mensal (8%)', value: `R$ ${res.monthlyDeposit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Total Acumulado', value: `R$ ${res.totalDeposit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Período', value: `${res.months} meses` }
    ]);
  };

  const hasResult = resultState !== null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-category-work rounded-full border border-theme-border mb-4">
          <span className="text-2xl">🏦</span>
          <span className="text-sm font-black text-category-work uppercase tracking-wider">FGTS</span>
        </div>
        <p className="text-theme-muted text-sm">Calcule depósitos mensais e saque-aniversário do FGTS</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Informações para Cálculo</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Salário (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-2xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={salary}
                onChange={e => setSalary(maskNumber(e.target.value))}
                placeholder="Ex: 5.000,00"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400 pl-12"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Salário base para cálculo do FGTS</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Período (meses)</label>
            <input
              type="text"
              value={months}
              onChange={e => setMonths(maskInteger(e.target.value))}
              placeholder="Ex: 12"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Quantos meses de depósito</div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-category-work rounded-xl border border-theme-border">
          <h4 className="text-sm font-bold text-category-work mb-2">Alíquota FGTS</h4>
          <div className="text-xs text-theme-muted">
            Depósito padrão (CLT): <span className="font-bold text-theme-text">8%</span> do salário.
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular FGTS
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-emerald-500 to-teal-500 border-teal-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">🏦</div>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Depósito Mensal</p>
                <p className="text-2xl font-black">R$ {resultState.monthlyDeposit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.rate}% do salário</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Total Acumulado</p>
                <p className="text-2xl font-black">R$ {resultState.totalDeposit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.months} meses</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">🎂 Saque-Aniversário</p>
              <div className="space-y-1 text-xs">
                {resultState.withdrawalRates.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between">
                    <span>{item.years} ano{item.years > 1 ? 's' : ''}</span>
                    <span className="font-bold">{item.rate}% disponível</span>
                  </div>
                ))}
              </div>
              <p className="text-xs opacity-70 mt-2">Multas e impostos podem ser aplicados no saque</p>
            </div>
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-theme-muted-section rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-theme-input rounded-full flex items-center justify-center text-2xl mb-3">🏦</div>
          <p className="font-bold text-base">Preencha os dados para ver o cálculo</p>
        </div>
      )}
    </div>
  );
};

export default FGTSCalculator;
